import React from "react";

import { calculateMaxEstimatedCost } from "ComparePlans/calculateCost";
import EstimatedCosts from "ComparePlans/Components/EstimatedCosts";
import MoreDetails from "ComparePlans/Components/MoreDetails";
import { getDependents, getEmployeeForm } from "EmployeeApplication/api";
import { useSelector } from "react-redux";
import formatCurrency, { notCoveredOrFormatCurrency } from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";
import getTooltip from "Utils/getTooltip";

import BodyText from "Cobalt/BodyText";
import { CardSection } from "Cobalt/Card";
import { Title } from "Cobalt/Header";
import { Popover } from "Cobalt/Popover";
import { Table, TD, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { Div, Span } from "Common/HTML";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";
import PlanRecommendation from "Common/PlanRecommendation";
import SIOverlayTrigger from "Common/SIOverlayTrigger";

import { MedicalEnrollOrDeclineButton } from "./medical.modals";

import type { MedicalPlan, PlanCost } from "Utils/types";

const AgeCostRows = ({ plansData }) => {
  const dependents = useSelector(getDependents);
  const employeeForm = useSelector(getEmployeeForm);
  const spousesOrDomesticPartners = dependents
    .filter((d) => d.dependentType === "spouse" || d.dependentType === "domestic_partner")
    .sort(({ bornAt: a }, { bornAt: b }) => a - b);
  const children = dependents.filter((d) => d.dependentType === "child").sort(({ bornAt: a }, { bornAt: b }) => a - b);
  return (
    <>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>{formatNameFL(employeeForm)}</TD>
        {plansData.map(({ plan, planCost }) => {
          const breakdown = planCost.costBreakdowns.age;
          return (
            <TD key={plan.id} align="left">
              {formatCurrency(breakdown.employeeCost)}
            </TD>
          );
        })}
      </TR>
      {spousesOrDomesticPartners.map((spouse) => (
        <TR highlightFirstCell={false} withBorder={false} key={spouse.id}>
          <TD>{formatNameFL(spouse)}</TD>
          {plansData.map(({ plan, planCost }) => (
            <TD key={plan.id} align="left">
              {formatCurrency(planCost.pricing[spouse.id])}/mo
            </TD>
          ))}
        </TR>
      ))}
      {children.map((child) => (
        <TR highlightFirstCell={false} withBorder={false} key={child.id}>
          <TD>{formatNameFL(child)}</TD>
          {plansData.map(({ plan, planCost }) => (
            <TD key={plan.id} align="left">
              {formatCurrency(planCost.pricing[child.id])}/mo
            </TD>
          ))}
        </TR>
      ))}
    </>
  );
};

const SEGMENT_LABELS = {
  eeOnly: "Just Me",
  eeSpouse: "Me and My Spouse",
  eeChildren: "Me and My Children",
  eeFamily: "Me, My Spouse, and My Children",
};

// One plan's cost lines, read from that plan's own pricing type. Age-rated plans price
// each family member; segment-rated plans price family tiers, so the two carry different
// labels and cannot share a row grid.
const PlanCostLines = ({ planCost }) => {
  const dependents = useSelector(getDependents);
  const employeeForm = useSelector(getEmployeeForm);

  if (planCost?.pricingType === "age") {
    const spousesOrDomesticPartners = dependents
      .filter((d) => d.dependentType === "spouse" || d.dependentType === "domestic_partner")
      .sort(({ bornAt: a }, { bornAt: b }) => a - b);
    const children = dependents.filter((d) => d.dependentType === "child").sort(({ bornAt: a }, { bornAt: b }) => a - b);
    return (
      <>
        <Div>
          {formatNameFL(employeeForm)}: {formatCurrency(planCost.costBreakdowns?.age?.employeeCost)}/mo
        </Div>
        {[...spousesOrDomesticPartners, ...children].map((dependent) => (
          <Div key={dependent.id}>
            {formatNameFL(dependent)}: {formatCurrency(planCost.pricing[dependent.id])}/mo
          </Div>
        ))}
      </>
    );
  }

  return (
    <>
      {Object.entries(SEGMENT_LABELS).map(([segment, label]) =>
        planCost?.costBreakdowns?.[segment] ? (
          <Div key={segment}>
            {label}: {formatCurrency(planCost.costBreakdowns[segment].employeeTotalCost)}/mo
          </Div>
        ) : null,
      )}
    </>
  );
};

// Used when the comparison mixes age-rated and segment-rated plans: each plan renders its
// own cost lines in its own cell rather than sharing one set of row labels.
const MixedPricingCostRow = ({ plansData }) => (
  <TR highlightFirstCell={false} withBorder={false}>
    <TD>Cost Breakdown</TD>
    {plansData.map(({ plan, planCost }) => (
      <TD key={plan.id} align="left">
        <PlanCostLines planCost={planCost} />
      </TD>
    ))}
  </TR>
);

const SegmentCostRows = ({ plansData }) => {
  return (
    <>
      {["eeOnly", "eeSpouse", "eeChildren", "eeFamily"].map((segment) => {
        // Assuming all plans have the same pricing structure.
        const breakdowns = plansData[0].planCost.costBreakdowns;
        if (breakdowns[segment] === undefined) {
          return null;
        }
        return (
          <TR key={segment} highlightFirstCell={false} withBorder={false}>
            <TD>{SEGMENT_LABELS[segment]}</TD>
            {plansData.map(({ plan, planCost }) => (
              <TD key={plan.id} align="left">
                {formatCurrency(planCost.costBreakdowns[segment]?.employeeTotalCost)}
              </TD>
            ))}
          </TR>
        );
      })}
    </>
  );
};

// Minimum Essential Coverage (MEC) plans cover preventive care only and set no ceiling on what
// the member can spend, so they carry this sentinel in place of a real out-of-pocket maximum.
const UNLIMITED_OUT_OF_POCKET = 999999;

const calculateDisplayValues = (individual, family) => {
  if (!family) {
    return `${formatCurrency(individual)} per individual, ${formatCurrency(individual * 2)} per family`;
  }
  return `${formatCurrency(individual)} per individual, ${formatCurrency(family)} per family`;
};

interface PlanTermLabelPopoverProps {
  term: string;
  text?: string;
}

const PlanTermLabelPopover = ({ term, text }: PlanTermLabelPopoverProps) => (
  <SIOverlayTrigger
    placement="bottom"
    overlay={
      <Popover showBackdrop={false} className="plan-term-tooltip" id={term}>
        {getTooltip(term)}
      </Popover>
    }
    trackType="dashed-plan-term"
    trackId={term}
  >
    <span className="SIOverlayTrigger__base">{text || term}</span>
  </SIOverlayTrigger>
);

interface Props {
  plans: MedicalPlan[];
  planCosts: PlanCost[];
}

export const MedicalMultiplePlans = ({ plans, planCosts }: Props) => {
  // Pair up objects, just for sanity's sake.
  const plansData = plans.map((plan) => ({
    plan,
    planCost: planCosts.find((planCost) => planCost.planID === plan.id),
  }));

  // Pricing type is a property of each plan's own rate sheet, so one comparison can hold both.
  const hasMixedPricing = new Set(plansData.map(({ planCost }) => planCost?.pricingType)).size > 1;

  const renderCostRows = () => {
    if (hasMixedPricing) return <MixedPricingCostRow plansData={plansData} />;
    if (planCosts[0].pricingType === "age") return <AgeCostRows plansData={plansData} />;
    return <SegmentCostRows plansData={plansData} />;
  };

  return (
    <CardSection className="overflow-scroll">
      <Table>
        <tbody>
          <TR withBorder={false}>
            <TD width="150px">&nbsp;</TD>
            {plansData.map(({ plan }) => (
              <TD key={plan.id} align="left">
                <CarrierLogo plan={plan} maxSize={3} />
                <BodyText mb={4}>{plan.display_name}</BodyText>
              </TD>
            ))}
          </TR>
          <TR bgColor="gray" withBorder={false}>
            <TD>Premium Overview</TD>
            {plansData.map(({ plan }) => (
              <TD key={plan.id} align="left">
                Your Cost
                <InfoPopover
                  infoTitle="Payroll Contributions"
                  infoText="This is the amount you pay each month, taken directly out of your paycheck."
                  placement="top"
                  className="align-text-bottom"
                />
              </TD>
            ))}
          </TR>
          {renderCostRows()}
          <TR withBorder={false}>
            <TD>&nbsp;</TD>
            {plansData.map(({ plan, planCost }) => (
              <TD key={plan.id} align="left">
                <MedicalEnrollOrDeclineButton plan={plan} planCost={planCost} my={3} />
              </TD>
            ))}
          </TR>
          <TR bgColor="gray" withBorder={false}>
            <TD colSpan={99} align="left">
              Plan Overview
            </TD>
          </TR>
          <TR highlightFirstCell={false} withBorder={false}>
            <TD>Recommended For</TD>
            {plansData.map(({ plan }) => (
              <TD align="left">
                <Span py={2} className="inline-block">
                  <PlanRecommendation recommendation={plan.recommendation} />
                </Span>
              </TD>
            ))}
          </TR>
          <TR highlightFirstCell={false} withBorder={false}>
            <TD>
              <PlanTermLabelPopover term="Deductible" />
            </TD>
            {plansData.map(({ plan }) => (
              <TD align="left">{calculateDisplayValues(plan.deductible, plan.family_deductible)}</TD>
            ))}
          </TR>
          <TR highlightFirstCell={false} withBorder={false}>
            <TD>
              <PlanTermLabelPopover term="Annual Out-of-Pocket Limit" text="Maximum" />
            </TD>
            {plansData.map(({ plan }) => (
              <TD align="left">{plan.limit === UNLIMITED_OUT_OF_POCKET ? "Unlimited" : calculateDisplayValues(plan.limit, plan.family_limit)}</TD>
            ))}
          </TR>
          {["TX", "CA", "FL", "NY", "VA", "MD", "GA", "NJ", "IL", "AZ"].includes(plans[0].state) && (
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>Plan Network</TD>
              {plansData.map(({ plan }) => (
                <TD align="left">{plan.fine_print.network || plan.network}</TD>
              ))}
            </TR>
          )}
          <TR highlightFirstCell={false} withBorder={false}>
            <TD>
              <PlanTermLabelPopover term="Estimated Doctor Visit" text="Doctor Visits" />
            </TD>
            {plansData.map(({ plan }) => (
              <TD align="left">{notCoveredOrFormatCurrency(plan.estimated_costs.pcp_visit)}</TD>
            ))}
          </TR>
          <TR highlightFirstCell={false} withBorder={false}>
            <TD>
              <PlanTermLabelPopover term="Estimated Prescription Drugs" text="Prescription Drugs" />
            </TD>
            {plansData.map(({ plan }) => (
              <TD align="left">
                {notCoveredOrFormatCurrency(plan.estimated_costs.generic_drugs)} - {notCoveredOrFormatCurrency(plan.estimated_costs.brand_drugs)}
              </TD>
            ))}
          </TR>
          <TR highlightFirstCell={false} withBorder={false}>
            <TD>
              <PlanTermLabelPopover term="Estimated Hospitalization" text="Hospitalization" />
            </TD>
            {plansData.map(({ plan }) => (
              // An uncovered service arrives as text, and the estimated cost is arithmetic, so it has
              // to be passed through before the math turns it into NaN.
              <TD align="left">
                {typeof plan.estimated_costs.hospitalization === "number"
                  ? formatCurrency(calculateMaxEstimatedCost(plan, "hospitalization"))
                  : notCoveredOrFormatCurrency(plan.estimated_costs.hospitalization)}
              </TD>
            ))}
          </TR>
          <TR withBorder={false}>
            <TD>Summary of Benefits & Coverage</TD>
            {plansData.map(({ plan }) => (
              <TD align="left">
                {plan.has_sbc && (
                  <a href={plan.sbc_url} target="_blank" rel="noreferrer">
                    <Icon flavor="solid" icon="download" mr={3} />
                    Summary of Benefits and Coverage
                  </a>
                )}
              </TD>
            ))}
          </TR>
        </tbody>
        <EstimatedCosts heading={<Title mt={3}>Estimated Costs</Title>} plans={plansData.map(({ plan }) => plan)} />
        <MoreDetails
          tbodyClassName="ee-app-multiple-plans__more-details"
          heading={<Title mt={3}>Coverage Details</Title>}
          plans={plansData.map(({ plan }) => plan)}
        />
      </Table>
    </CardSection>
  );
};
